import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Layout, Table, Typography } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { renderDpatEvidenceLink, rowsHaveAllEvidenceLinks } from "utils/renderDpatEvidenceLink";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";
const GASupport = forwardRef(({
  year,
  cededRevenueUtilisationData,
  subStructureActivityData,
  cededRevenueUtilisationScore,
  substructureExpendature,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(4);
  const [conclusion, setConclusion] = useState("");

  const resolved = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return null;
    return {
      cededRevenueUtilisationData: savedAssessmentData.cededRevenueUtilisationData ?? cededRevenueUtilisationData,
      subStructureActivityData: savedAssessmentData.subStructureActivityData ?? subStructureActivityData,
      cededRevenueUtilisationScore: savedAssessmentData.cededRevenueUtilisationScore ?? cededRevenueUtilisationScore,
      substructureExpendature: savedAssessmentData.substructureExpendature ?? substructureExpendature,
      scorei: savedAssessmentData.scorei ?? 0,
      maxScore: savedAssessmentData.maxScore ?? 4,
      conclusion: savedAssessmentData.conclusion ?? "",
    };
  }, [
    isAssessmentStarted,
    savedAssessmentData,
    cededRevenueUtilisationData,
    subStructureActivityData,
    cededRevenueUtilisationScore,
    substructureExpendature,
  ]);

  const displayCeded = resolved?.cededRevenueUtilisationData ?? cededRevenueUtilisationData;
  const displayActivity = resolved?.subStructureActivityData ?? subStructureActivityData;
  const displayCededScore = resolved?.cededRevenueUtilisationScore ?? cededRevenueUtilisationScore;
  const displaySubExp = resolved?.substructureExpendature ?? substructureExpendature;
  const displayScore = resolved?.scorei ?? score;
  const displayMaxScore = resolved?.maxScore ?? maxScore;
  const displayConclusion = resolved?.conclusion ?? conclusion;

  const cededAmountUtilizationColumns = [
    { title: "Name of substructure", dataIndex: "name", key: "name" },
    { title: "Amount of Ceded Revenue Received (GHS) - A", dataIndex: "collected", key: "collected" },
    { title: "Amount of Ceded Revenue utilized for Community Activities (GHS)", dataIndex: "ceded", key: "ceded" },
    { title: "% of Amount utilized for Community Activities ", dataIndex: "percentage", key: "percentage" }
  ];

  const subStructureActivityColumns = useMemo(
    () => buildSearchableColumns(
      [
        { title: "No", dataIndex: "no", key: "no" },
        { title: "Activities ", dataIndex: "activities", key: "collected" },
        { title: "Substructure", dataIndex: "name", key: "name" },
        { title: "Funding Source", dataIndex: "source", key: "source" },
        { title: "Amount Utilized", dataIndex: "amount", key: "amount" },
        {
          title: "Evidence",
          dataIndex: "evidence",
          key: "evidence",
          render: (value) => renderDpatEvidenceLink(value, "View Evidence"),
        },
      ],
      displayActivity ?? [],
      {
        textSearchKeys: ["activities", "name", "source", "amount"],
        selectFilterKeys: ["source"],
      },
    ),
    [displayActivity],
  );

  const subExpendatureColumns = [
    { title: "DACF Releases", dataIndex: "quarter", key: "quarter" },
    { title: "Amount Released", dataIndex: "amountReleased", key: "amountReleased" },
    { title: "2% of Amount Released", dataIndex: "twoPercentReleased", key: "twoPercentReleased" },
    { title: "Total Amount spent on substructures", dataIndex: "spentOnSubstructure", key: "spentOnSubstructure" },
    { title: "% spent on substructures", dataIndex: "percentageSpentSubstructure", key: "percentageSpentSubstructure" }
  ];

  useEffect(() => {
    if (isAssessmentStarted) return;
    const subs = substructureExpendature?.data?.length > 0 ? substructureExpendature?.data[0] : 0;

    const amountNinety = calculateNinetyPercentOfAmount(subs?.twoPercentReleased);
    const amountSpendOnSub = calculateSubExpendature();
    const activityEvidenceComplete = rowsHaveAllEvidenceLinks(
      displayActivity ?? [],
      ["evidence"],
    );

    if (amountSpendOnSub !== 0 && amountNinety !== 0) {
      if (amountSpendOnSub >= amountNinety && activityEvidenceComplete) {
        setScore(2);
      } else {
        setScore(0);
      }
    } else {
      setScore(0);
    }


    setConclusion(`Amount spent on substructure is ${amountSpendOnSub} and 90% of the 2% released is ${amountNinety}`);
  }, [substructureExpendature, displayActivity, isAssessmentStarted]);

  const calculateSubExpendature = () => {
    let amountSpent = 0;

    subStructureActivityData?.forEach(sub => {
      if (sub.source === 'DACF') {
        const amt = sub.amount;
        amountSpent += parseFloat(amt);
      }
    });

    return amountSpent;
  };

  const calculateNinetyPercentOfAmount = (amount) => {
    const totalAmount = parseFloat(amount);

    if (isNaN(totalAmount)) {
      return 0;
    }

    return ((totalAmount * 90) / 100).toFixed(2);
  };

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI1",
      area: "Management Coordination – Implementation of Service Delivery Decisions",
      maxScore: displayMaxScore,
      cededRevenueUtilisationData: displayCeded,
      subStructureActivityData: displayActivity,
      scorei: displayScore,
      substructureExpendature: displaySubExp,
    }),
  }));

  const DPAT_SLUG = "sdi1.0-1.3";

  return (
    <Comment
      data={displayCeded}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi1.0-1.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={displayMaxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 1.0-1.3i Actual Score" score={displayCededScore >= 50 ? '2' : '0'} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 1.0-1.3ii Actual Score" score={displayScore} />
          </IndicatorScoreSection>
          <p level={5} style={{ marginTop: "10px" }}>
            {displayConclusion}
          </p>

          <Title level={4} style={{ marginTop: "30px" }}>Findings / Observations & Conclusion</Title>
          <Content>
            We received and reviewed information on the activities of established sub-structures and noted the following:
          </Content>

          <Title level={5} style={{ marginTop: "30px" }}>I. Evidence of utilization of ceded revenue</Title>
          {displayCeded && <Table
            columns={cededAmountUtilizationColumns}
            dataSource={displayCeded}
            pagination={false} bordered />}

          <Title level={5} style={{ marginTop: "30px" }}>II. Selected Activities that Benefit the Community</Title>
          {displayActivity?.length > 0 && <Table
            columns={subStructureActivityColumns}
            dataSource={displayActivity}
            rowKey={(record) => record.key ?? record.no}
            pagination={getDpatTablePagination("activities")}
            bordered
          />}

          <Title level={5} style={{ marginTop: "30px" }}>III. Evidence of DACF expenditure on substructures</Title>
          {displaySubExp && <Table
            columns={subExpendatureColumns}
            dataSource={displaySubExp?.data}
            pagination={false} bordered />}
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default GASupport;